// Hostname -> brand resolution for the public booking site. One Cloudflare
// Pages deployment serves every brand domain; this module decides which
// brand's identity/copy to render based on where the request landed.
//
// This is presentation-only. It does not scope API data by itself — pair it
// with a `brand` query param on hostel-fetching endpoints once the backend
// supports filtering (see facilities.service.ts getHostels()).

export type TenantBrand = 'EASY_AND_QUIET' | 'AN_NOOR';

export interface TenantConfig {
  brand: TenantBrand;
  displayName: string;
  /** PLACEHOLDER asset path — no dedicated brand logo has been supplied yet. */
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  heroCopy: {
    headline: string;
    subtitle: string;
  };
  footerCopy: {
    blurb: string;
    supportEmail: string;
  };
  /** Drives <title>/meta description and OG/Twitter tags — see functions/_middleware.ts. */
  seo: {
    title: string;
    description: string;
    /** Absolute URL — social crawlers don't resolve relative paths. */
    ogImageUrl: string;
  };
}

const DEFAULT_BRAND: TenantBrand = 'EASY_AND_QUIET';

export const TENANTS: Record<TenantBrand, TenantConfig> = {
  // Copy adapted from the existing LandingPage.tsx, with the An Noor mention
  // dropped now that each brand's site only names its own hostels.
  EASY_AND_QUIET: {
    brand: 'EASY_AND_QUIET',
    displayName: 'Easy & Quiet Hostels',
    logoUrl: '/brand/easy-and-quiet/logo.svg',
    primaryColor: '#0F766E',
    accentColor: '#14B8A6',
    heroCopy: {
      headline: 'Your Student Accommodation Starts Here.',
      subtitle:
        "Create your Student Accommodation Account to access our secure hostel booking portal. Available exclusively to eligible students in good standing with the University, you can browse Easy & Quiet Hostel (Main Campus) and Easy & Quiet Hostel (College of Health Science), select your preferred bed space, make a secure payment, and confirm your booking—all from one convenient platform.",
    },
    footerCopy: {
      blurb: 'Official student accommodation booking for Easy and Quiet hostels.',
      supportEmail: 'support@easyandquiethostel.com',
    },
    seo: {
      title: 'Easy and Quiet Hostel — Student Hostel Booking Portal',
      description:
        'Official Easy and Quiet Hostel booking portal. Browse Easy and Quiet hostels, reserve your bed space, and pay securely online from one student portal.',
      ogImageUrl: 'https://easyandquiethostel.com.ng/og-image.png',
    },
  },
  // Drafted to mirror the Easy & Quiet copy structure — this brand has no
  // existing landing page to adapt from, so treat this as a first draft for
  // the client to sign off on, not confirmed brand voice.
  AN_NOOR: {
    brand: 'AN_NOOR',
    displayName: 'An Noor Hostel',
    logoUrl: '/brand/an-noor/logo.svg',
    // Building is painted yellow, pillar is sky blue (per client). Yellow is
    // deepened to yellow-600 here so it still passes contrast as button/text
    // color on a light background — swap if the client wants the literal
    // brighter yellow used decoratively instead.
    primaryColor: '#CA8A04',
    accentColor: '#0EA5E9',
    heroCopy: {
      headline: 'Your Student Accommodation Starts Here.',
      subtitle:
        "Create your Student Accommodation Account to access An Noor Hostel's secure booking portal. Available exclusively to eligible students in good standing with the University, browse available bed spaces, select your preferred room, make a secure payment, and confirm your booking—all from one convenient platform.",
    },
    footerCopy: {
      blurb: 'Official student accommodation booking for An Noor Hostel.',
      supportEmail: 'support@annoorhostel.com',
    },
    seo: {
      title: 'An Noor Hostel — Student Hostel Booking Portal',
      description:
        'Official An Noor Hostel booking portal. Browse available bed spaces, reserve your room, and pay securely online from one student portal.',
      ogImageUrl: 'https://annoor.easyandquiethostel.com.ng/og-image-an-noor.png',
    },
  },
};

// Real domains must still be attached as custom domains to the Cloudflare
// Pages project ("hostel-management-fe") before they'll resolve.
const HOSTNAME_BRAND_MAP: Record<string, TenantBrand> = {
  'easyandquiethostel.com.ng': 'EASY_AND_QUIET',
  'www.easyandquiethostel.com.ng': 'EASY_AND_QUIET',
  'annoor.easyandquiethostel.com.ng': 'AN_NOOR',
};

export function resolveTenant(hostname: string = window.location.hostname): TenantConfig {
  const brand = HOSTNAME_BRAND_MAP[hostname] ?? DEFAULT_BRAND;
  return TENANTS[brand];
}
